import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import Navbar from '../components/Navbar';

// Map of province codes to full names
const provinceNames: { [key: string]: string } = {
  'AB': 'Alberta',
  'BC': 'British Columbia',
  'MB': 'Manitoba',
  'NB': 'New Brunswick',
  'NL': 'Newfoundland and Labrador',
  'NS': 'Nova Scotia',
  'NT': 'Northwest Territories',
  'NU': 'Nunavut',
  'ON': 'Ontario',
  'PE': 'Prince Edward Island',
  'QC': 'Quebec',
  'SK': 'Saskatchewan',
  'YT': 'Yukon'
};

const Leaderboards = () => {
  // State for leaderboards
  const [provincesRanking, setProvincesRanking] = useState<any[]>([]);
  const [teamsRanking, setTeamsRanking] = useState<any[]>([]);
  const [individualsRanking, setIndividualsRanking] = useState<any[]>([]);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState<'provinces' | 'teams' | 'individuals'>('provinces');
  
  // Loading state
  const [loading, setLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        // Get provinces ranking
        const provincesRef = collection(db, 'provinces');
        const provincesQuery = query(
          provincesRef,
          orderBy('totalMiles', 'desc')
        );
        const provincesSnapshot = await getDocs(provincesQuery);
        
        const provincesList: any[] = [];
        provincesSnapshot.forEach((doc) => {
          const data = doc.data();
          provincesList.push({
            id: doc.id,
            name: provinceNames[doc.id] || doc.id,
            miles: data.totalMiles || 0,
            participantCount: data.participantCount || 0
          });
        });
        
        setProvincesRanking(provincesList);
        
        // Get top 10 teams
        const teamsRef = collection(db, 'teams');
        const teamsQuery = query(
          teamsRef,
          orderBy('totalMiles', 'desc'),
          limit(10)
        );
        const teamsSnapshot = await getDocs(teamsQuery);
        
        const teamsList: any[] = [];
        teamsSnapshot.forEach((doc) => {
          const data = doc.data();
          teamsList.push({
            id: doc.id,
            name: data.name,
            province: provinceNames[data.province] || data.province,
            miles: data.totalMiles || 0,
            memberCount: data.memberCount || 0
          });
        });
        
        setTeamsRanking(teamsList);
        
        // Get top 10 individuals
        const usersRef = collection(db, 'users');
        const usersQuery = query(
          usersRef,
          orderBy('totalMiles', 'desc'),
          limit(10)
        );
        const usersSnapshot = await getDocs(usersQuery);
        
        const usersList: any[] = [];
        usersSnapshot.forEach((doc) => {
          const data = doc.data();
          usersList.push({
            id: doc.id,
            name: `${data.firstName} ${data.lastName}`,
            province: provinceNames[data.province] || data.province,
            miles: data.totalMiles || 0
          });
        });
        
        setIndividualsRanking(usersList);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
        setLoading(false);
      }
    };
    
    fetchLeaderboardData();
  }, []);
  
  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Leaderboards</h1>
        
        {/* Tabs */}
        <div className="mb-6">
          <div className="sm:hidden">
            <label htmlFor="leaderboard-tabs" className="sr-only">Select a leaderboard</label>
            <select
              id="leaderboard-tabs"
              name="leaderboard-tabs"
              className="block w-full rounded-md border-gray-300 focus:border-primary focus:ring-primary"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as any)}
            >
              <option value="provinces">Provincial Leaderboard</option>
              <option value="teams">Team Leaderboard</option>
              <option value="individuals">Individual Leaderboard</option>
            </select>
          </div>
          <div className="hidden sm:block">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('provinces')}
                  className={`
                    ${activeTab === 'provinces'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  `}
                >
                  Provincial Leaderboard
                </button>
                <button
                  onClick={() => setActiveTab('teams')}
                  className={`
                    ${activeTab === 'teams'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  `}
                >
                  Team Leaderboard
                </button>
                <button
                  onClick={() => setActiveTab('individuals')}
                  className={`
                    ${activeTab === 'individuals'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  `}
                >
                  Individual Leaderboard
                </button>
              </nav>
            </div>
          </div>
        </div>
        
        {/* Provincial Leaderboard */}
        {activeTab === 'provinces' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 bg-primary bg-opacity-10">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Provincial Leaderboard</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                All Canadian provinces and territories ranked by total miles walked.
              </p>
            </div>
            <div className="border-t border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Province/Territory
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Participants
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Miles
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {provincesRanking.map((province, index) => (
                    <tr key={province.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {province.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {province.participantCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {province.miles.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {provincesRanking.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Team Leaderboard */}
        {activeTab === 'teams' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 bg-secondary bg-opacity-10">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Team Leaderboard</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Top 10 teams ranked by total miles walked.
              </p>
            </div>
            <div className="border-t border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Team Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Province
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Members
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Miles
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teamsRanking.map((team, index) => (
                    <tr key={team.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {team.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {team.province}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {team.memberCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {team.miles.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {teamsRanking.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Individual Leaderboard */}
        {activeTab === 'individuals' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 bg-accent bg-opacity-10">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Individual Leaderboard</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Top 10 individuals ranked by total miles walked.
              </p>
            </div>
            <div className="border-t border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Province
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Miles
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {individualsRanking.map((individual, index) => (
                    <tr key={individual.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {individual.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {individual.province}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {individual.miles.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {individualsRanking.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboards; 